import axios from "axios";

import type { components } from "@/types/api.generated";
import apiClient from "./client";

/**
 * A estrutura organizacional validada com o cliente, no Diagnóstico (#1342).
 *
 * Aliases sobre os tipos gerados do OpenAPI, como em `diagnostic.ts`: o contrato
 * mora no backend, e um tipo escrito à mão divergiria dele em silêncio.
 */
export type OrgValidationTree = components["schemas"]["DiagnosticOrgStructureResponse"];
export type OrgValidationNode = components["schemas"]["OrgValidationNode"];
export type OrgStructureLevelRow = components["schemas"]["OrgValidationLevel"];
export type OrgCompany = components["schemas"]["OrgCompany"];
export type OrgValidationUpdate = components["schemas"]["OrgValidationUpdate"];
export type OrgValidationResult = components["schemas"]["OrgValidationResult"];
export type OrgValidationBatchUpdate = components["schemas"]["OrgValidationBatchUpdate"];
export type OrgValidationBatchResult = components["schemas"]["OrgValidationBatchResult"];

/** Pendente é `null`, e não um quarto valor: a linha da validação é esparsa. */
export type OrgUnitDecision = NonNullable<OrgValidationNode["effective_decision"]>;

/**
 * `GET /api/projects/{projectId}/diagnostic/org-structure`.
 *
 * Projeto sem a extração dos níveis responde 200 com `source: null` e listas
 * vazias, nunca 404: a tela mostra o estado vazio com o motivo.
 */
export async function getDiagnosticOrgStructure(projectId: string): Promise<OrgValidationTree> {
  const res = await apiClient.get<OrgValidationTree>(
    `/api/projects/${projectId}/diagnostic/org-structure`,
  );
  return res.data;
}

/**
 * `PUT .../org-structure/validation` — grava a validação de uma unidade.
 *
 * Só os campos enviados mudam, e `"decision": null` volta para Pendente. Ao tirar
 * uma unidade de "fora do escopo", `reset_to_pending` lista os descendentes que
 * voltaram a Pendente na mesma transação.
 */
export async function putOrgUnitValidation(
  projectId: string,
  body: OrgValidationUpdate,
): Promise<OrgValidationResult> {
  const res = await apiClient.put<OrgValidationResult>(
    `/api/projects/${projectId}/diagnostic/org-structure/validation`,
    body,
  );
  return res.data;
}

/**
 * `PUT .../org-structure/validation/batch` — a mesma decisão de escopo em várias
 * unidades, numa transação só: grava tudo ou nada.
 *
 * `covered_by_parent` são as do lote que passaram a herdar o "fora" de um nível
 * acima que está no lote; `affected_below`, as de fora do lote cuja decisão
 * efetiva mudou (inclui as de `reset_to_pending`).
 */
export async function putOrgUnitValidationBatch(
  projectId: string,
  body: OrgValidationBatchUpdate,
): Promise<OrgValidationBatchResult> {
  const res = await apiClient.put<OrgValidationBatchResult>(
    `/api/projects/${projectId}/diagnostic/org-structure/validation/batch`,
    body,
  );
  return res.data;
}

/**
 * O que deu errado no PUT, nos termos da regra do backend:
 *
 * - `not_found` (404): a chave não é mais unidade da árvore, porque a extração mudou;
 * - `inherited` (409): a unidade está fora do escopo pelo nível acima;
 * - `invalid` (422): campo que não vale para o tipo da unidade, ou valor fora do vocabulário;
 * - `forbidden` (403): o papel `user` vê e não edita;
 * - `failed`: o resto, inclusive falha de rede, que não tem status.
 */
export type OrgValidationErrorKind = "not_found" | "inherited" | "invalid" | "forbidden" | "failed";

const ERROR_KIND_BY_STATUS: Partial<Record<number, OrgValidationErrorKind>> = {
  403: "forbidden",
  404: "not_found",
  409: "inherited",
  422: "invalid",
};

export function orgValidationErrorKind(error: unknown): OrgValidationErrorKind {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return (status !== undefined && ERROR_KIND_BY_STATUS[status]) || "failed";
}

/**
 * Quantas unidades o erro do lote nomeia (`detail.items`, no formato do
 * Gerenciamento). `null` quando o erro não traz a lista: o PUT de uma unidade,
 * o 422 do Pydantic, a falha de rede.
 */
export function orgValidationErrorItems(error: unknown): number | null {
  if (!axios.isAxiosError(error)) return null;
  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  if (detail === null || typeof detail !== "object" || Array.isArray(detail)) return null;
  const items = (detail as { items?: unknown }).items;
  return Array.isArray(items) ? items.length : null;
}
